// FIRST - Faro patches global fetch for tracing, so it must boot before any api
// call is made. See lib/observability/faro-client.ts.
import { initFaro } from '@/lib/observability/faro-client';
import { initUmami } from '@/lib/analytics/umami-client';
import { env } from '@/env';

import { queryClient } from '@/api/query-client';
import { QueryClientProvider } from '@tanstack/react-query';
import { init as initCrikket } from '@toppynl/crikket-capture';
import { Capacitor } from '@capacitor/core';
import Framework7React from 'framework7-react';
import Framework7 from 'framework7/bundle';
import React from 'react';
import { createRoot } from 'react-dom/client';

import 'framework7/css/bundle';
import 'framework7-icons/css/framework7-icons.css';

import { startAuthRefreshLoop } from '@/auth/token-interceptor';
import AppErrorBoundary from '@/components/AppErrorBoundary';
import { createDraggableCrikketMount } from '@/lib/draggable-crikket-launcher';
import { AppUpdaterService } from '@/lib/services/app-updater-service';
import { installStaleChunkReload } from '@/lib/stale-chunk-reload';
import App from './App';
// Shared custom theme - brands both the ios and md base identically. Imported
// after the F7 bundle (so it overrides defaults) and before styles.css (so
// component-specific tweaks still win).
import './theme/restaurant-theme.css';
import './styles.css';

// ── React 19 × Framework7 DOM-reconciliation guard ────────────────────────
// Framework7 mutates the DOM directly (page transitions, dialogs, tooltips,
// list virtualization), so by the time React's commit phase tries to remove or
// re-insert a node, F7 may have already moved or detached it. React 19 throws a
// hard `NotFoundError: Failed to execute 'removeChild'/'insertBefore'` in that
// case and unmounts the whole tree. Making these two operations no-op when the
// node isn't actually a child of the target turns the fatal crash into a
// harmless console note - the same well-known workaround used for Google
// Translate and other libraries that move DOM out from under React. Installed
// before any render so it covers the very first commit.
// Start Faro (errors + tracing) before anything renders or fetches.
initFaro(env.deployEnv ?? 'production', import.meta.env.VITE_GIT_SHA ?? 'dev');
initUmami();

// Then the stale-chunk guard, before the first lazy import can fail: a tab left
// open across a deploy asks for hashed assets the new build no longer serves.
installStaleChunkReload();

if (typeof Node === 'function' && Node.prototype) {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      if (import.meta.env.DEV) {
        console.warn('[dom-guard] skipped removeChild: node is not a child of the target', child);
      }
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      if (import.meta.env.DEV) {
        console.warn(
          '[dom-guard] skipped insertBefore: reference node has a different parent',
          referenceNode,
        );
      }
      return newNode;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}

// `Framework7.use` is F7's plugin registration, not the React `use` hook - the
// rules-of-hooks heuristic only sees the `use` name.
// eslint-disable-next-line react-hooks/rules-of-hooks
Framework7.use(Framework7React);

// Surface the deployed version in the console so "what's live" is one glance
// away; the same identity is served as /version.json for tooling.
console.info(
  `restaurant-dashboard ${import.meta.env.VITE_GIT_SHA ?? 'dev'}` +
    (import.meta.env.VITE_BUILD_TIME ? ` (built ${import.meta.env.VITE_BUILD_TIME})` : ''),
);

// Keep the session alive: proactively refresh the access token before it expires
// (which also resets Keycloak's SSO-idle timer), so an open tab isn't bounced to
// the login screen after an idle gap.
startAuthRefreshLoop();

// Initialize Crikket bug reporting widget. Gated solely by the key - only the
// dev (staging-equivalent) build config supplies it, so prod builds skip init at
// runtime. See config/build.staging.json and docker/dashboard.Dockerfile.
if (env.crikketKey) {
  initCrikket({
    key: env.crikketKey,
    host: env.crikketHost || 'https://crikket.internal.96digital.de',
    mountTarget: createDraggableCrikketMount(),
  });
}

const container = document.getElementById('app');
if (!container) {
  throw new Error('Root element not found');
}

// Placeholder shown only while the OTA check runs on native. Plain DOM rather
// than a React render: the whole point is that React is NOT mounted yet.
function showUpdateSplash(el: HTMLElement): void {
  el.innerHTML =
    '<div style="display:flex;align-items:center;justify-content:center;height:100dvh;' +
    'font:400 15px/1.5 system-ui,-apple-system,sans-serif;color:#8e8e93">Updating…</div>';
}

/**
 * Boot in two steps, because the OTA swap is destructive.
 *
 * `CapacitorUpdater.set()` reloads the webview the instant a new bundle lands,
 * killing this JavaScript context. Doing that with a mounted React tree is what
 * showed the error boundary on the first launch of a fresh install (the one
 * launch that always finds a newer bundle). So: check and swap FIRST, render
 * second. On web the check is an immediate no-op, so there's no added latency
 * and no splash.
 */
async function boot(el: HTMLElement): Promise<void> {
  const updater = AppUpdaterService.getInstance();

  if (Capacitor.isNativePlatform()) showUpdateSplash(el);
  // Returns only when there's nothing to install (or the check timed out) - an
  // available update reloads the app from inside this call and never comes back.
  await updater.applyUpdateBeforeRender();
  el.replaceChildren();

  // Global StrictMode. Safe because we don't use F7 `browserHistory` (its history
  // restore is what clashed with StrictMode's dev double-mount, #4278); the
  // `Node.prototype` guards above neutralise F7's direct-DOM churn, and the
  // framework7-react page-loader patch (#4387) keeps page transitions correct.
  // The boundary sits OUTSIDE QueryClientProvider so it also catches a throw from
  // the provider itself, and inside StrictMode so dev double-mounting still
  // exercises it.
  createRoot(el).render(
    <React.StrictMode>
      <AppErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </AppErrorBoundary>
    </React.StrictMode>,
  );

  // Only now that a tree is mounted does a swap need the isApplyingUpdate guard.
  updater.startUpdatePolling();
  updater.startResumeAcknowledgement();
}

void boot(container);
